<script>
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';
  import calendarize from 'calendarize';

  import { Icon } from '@client/components/icon';

  import { staticColors } from '@common/utils/colors';

  /**
   * Current year for date-related operations.
   * @type {number}
   * @default Current year
   */
  export let year = new Date().getFullYear();

  /**
   * Current month for date-related operations (0-indexed, January)
   * @type {number}
   * @default Current month
   */
  export let month = new Date().getMonth();

  /**
   * Day offset for the start of the week (0-indexed, Sunday).
   * @type {number}
   * @default 0 - Sunday
   */
  export let offset = 0;

  /**
   * Placeholder for the current date. To be set dynamically during runtime.
   * @type {Date}
   * @default Date - today's date.
   */
  export let today = new Date();

  /**
   * Array representing the days of the week.
   * @type {Array}
   * @default '[Sun, Mon, Tue, Wed, Thu, Fri, Sat]'
   */
  export let labels = [
    $format('label.DAYS_SHORTENED.SUN'),
    $format('label.DAYS_SHORTENED.MON'),
    $format('label.DAYS_SHORTENED.TUE'),
    $format('label.DAYS_SHORTENED.WED'),
    $format('label.DAYS_SHORTENED.THU'),
    $format('label.DAYS_SHORTENED.FRI'),
    $format('label.DAYS_SHORTENED.SAT')
  ];

  /**
   * Array containing the names of the months.
   * @type {Array}
   * @default '[January, February, March, April, May, June, July, August, September, October, November, December]'
   */
  export let months = [
    $format('label.MONTH_NAMES.JANUARY'),
    $format('label.MONTH_NAMES.FEBRUARY'),
    $format('label.MONTH_NAMES.MARCH'),
    $format('label.MONTH_NAMES.APRIL'),
    $format('label.MONTH_NAMES.MAY'),
    $format('label.MONTH_NAMES.JUNE'),
    $format('label.MONTH_NAMES.JULY'),
    $format('label.MONTH_NAMES.AUGUST'),
    $format('label.MONTH_NAMES.SEPTEMBER'),
    $format('label.MONTH_NAMES.OCTOBER'),
    $format('label.MONTH_NAMES.NOVEMBER'),
    $format('label.MONTH_NAMES.DECEMBER')
  ];

  /**
   * Object to store events keyed by date.
   * @type {Object}
   * @default {}
   */
  export let events = {};

  /**
   * Indicates whether future dates should be enabled.
   * @type {boolean}
   * @default false
   */
  export let enableFutureDates = true;

  /**
   * Indicates whether past dates should be enabled.
   * @type {boolean}
   * @default true
   */
  export let enablePastDates = true;

  /**
   * maxEventCount - Maximum number of events to display on a single day.
   * @type {number}
   */
  export let maxEventCount = 60;

  /**
   * Callback to execute on day click event
   * @type {Function}
   * @default () => {}
   */
  export let onDayClick = () => {};

  /**
   * Callback to execute on month change event
   * @type {Function}
   * @default () => {}
   */
  export let onMonthChange = () => {};

  /**
   * Callback to execute on day hover event
   * @type {Function}
   * @default () => {}
   */
  export let onDayHover = () => {};

  /**
   * Callback to execute on today click
   * @type {Function}
   * @default () => {}
   */
  export let onTodayClick = () => {};

  $: today_month = today && today.getMonth();
  $: today_year = today && today.getFullYear();
  $: today_day = today && today.getDate();

  let prev = calendarize(new Date(year, month - 1), offset);
  let current = calendarize(new Date(year, month), offset);
  let next = calendarize(new Date(year, month + 1), offset);

  const handleOnMonthChange = () => {
    let monthEvents = Object.keys(events).length && events[`${String(month + 1).padStart(2, '0')}-${year}`];

    if (monthEvents) {
      monthEvents = monthEvents['1'];
    }

    onMonthChange(month, year, '1', monthEvents || []);
  };

  const toPrev = () => {
    [current, next] = [prev, current];

    if (--month < 0) {
      month = 11;
      year--;
    }

    prev = calendarize(new Date(year, month - 1), offset);

    handleOnMonthChange();
  };

  const toNext = () => {
    [prev, current] = [current, next];

    if (++month > 11) {
      month = 0;
      year++;
    }

    next = calendarize(new Date(year, month + 1), offset);

    handleOnMonthChange();
  };

  const isToday = (day) => {
    return today && today_year === year && today_month === month && today_day === day;
  };

  const toToday = () => {
    today = new Date();
    month = today.getMonth();
    year = today.getFullYear();
    prev = calendarize(new Date(year, month - 1), offset);
    current = calendarize(new Date(year, month), offset);
    next = calendarize(new Date(year, month + 1), offset);

    handleOnMonthChange();

    onTodayClick();
  };

  const eventColors = staticColors.slice();

  const onClick = function (day) {
    const totalEvents = Object.keys(events).length ? events[`${String(month + 1).padStart(2, '0')}-${year}`][day] : [];

    onDayClick({
      dateLabel: `${months[month]} ${day}, ${year}`,
      date: {
        month: month + 1,
        day,
        year
      },
      events: totalEvents,
      colors: eventColors.slice(0, totalEvents.length)
    });
  };

  $: currentEvents = events[`${String(month + 1).padStart(2, '0')}-${year}`];
  $: disableFuture = !enableFutureDates && new Date().getFullYear() === year && new Date().getMonth() === month;
  $: disablePast = !enablePastDates && new Date().getFullYear() === year && new Date().getMonth() === month;
</script>

<div class="calendar">
  <header>
    <Button color="link" class="text-body" disabled={disablePast} on:click={toPrev} data-testid="prev">
      <Icon name="chevron-left" class="pointer" style="font-size: var(--font-size-xxxlarge)" />
    </Button>
    <h4>{months[month]} {year}</h4>
    <Button color="link" class="text-body" disabled={disableFuture} on:click={toNext} data-testid="next">
      <Icon name="chevron-right" class="pointer" style="font-size: var(--font-size-xxxlarge)" />
    </Button>
    <Button
      color="primary"
      class="rounded-pill position-absolute end-0 d-none d-md-flex"
      on:click={toToday}
      data-testid="today"
    >
      {$format('label.TODAY')}
    </Button>
  </header>

  <!-- svelte-ignore a11y-mouse-events-have-key-events -->
  <div class="month" role="button" tabindex="0">
    {#each labels as text, labelIndex (text)}
      <span class="label">{labels[(labelIndex + offset) % 7]}</span>
    {/each}

    {#each { length: 6 } as week, weekIndex (weekIndex)}
      {#if current[weekIndex]}
        {#each { length: 7 } as d, dayIndex (dayIndex)}
          {#if current[weekIndex][dayIndex] !== 0}
            <span
              class="date"
              role="button"
              tabindex="0"
              class:today={isToday(current[weekIndex][dayIndex])}
              class:events={currentEvents && currentEvents[current[weekIndex][dayIndex]]?.length}
              on:mouseenter={() =>
                onDayHover(
                  current[weekIndex][dayIndex],
                  currentEvents ? currentEvents[current[weekIndex][dayIndex]] : []
                )}
              on:mouseleave={() => onDayHover()}
              on:click={() => onClick(current[weekIndex][dayIndex])}
            >
              {#if currentEvents && currentEvents[current[weekIndex][dayIndex]]}
                <div class="events-total">{currentEvents[current[weekIndex][dayIndex]].length}</div>
                <div class="events">
                  {#if currentEvents[current[weekIndex][dayIndex]].length <= maxEventCount}
                    {#each currentEvents[current[weekIndex][dayIndex]] as event, eventIndex (eventIndex)}
                      <span
                        class="dot"
                        tabindex="0"
                        style="background-color: #{event.color || eventColors[eventIndex]}"
                        title={event.title}
                      />
                    {/each}
                  {/if}
                </div>
              {/if}
              {current[weekIndex][dayIndex]}
            </span>
          {:else if weekIndex < 1}
            <span class="date other">{prev[prev.length - 1][dayIndex]}</span>
          {:else}
            <span class="date other">{next[0][dayIndex]}</span>
          {/if}
        {/each}
      {/if}
    {/each}
  </div>
</div>

<style lang="scss" src="./calendar.scss"></style>
